/**
 * Share a technology from inside the platform, with a per-user tracked link.
 *
 * Every share goes through POST /api/v1/share-links: the server issues one
 * code per sharer per target (reused), records the share event (copy,
 * channel or native) and answers the public URL with `?s=<code>&utm_*`, so
 * the admin Shares page can tell who shared what, where, and who opened it.
 *
 * The first share of a tech waits for that URL; the link is then kept for
 * the tab, so later shares act at once and the event is recorded in the
 * background. Channels that open a window get a blank one synchronously
 * (inside the click) and are pointed at the target once the URL is known, so
 * popup blockers do not eat the share.
 *
 * LinkedIn (tracker, 2026-10-31). A web page cannot address one LinkedIn
 * chat, so on a computer the menu offers both: "Post on LinkedIn" (the post
 * composer, title and link written) and "Send in a LinkedIn message" (the
 * link is copied and LinkedIn's messaging opens on the chats, to paste into
 * one). On a phone, where LinkedIn's web composer does not survive the jump
 * into the app, "LinkedIn" opens the phone's share sheet, whose LinkedIn
 * entry offers "Send as private message" as well as a post; without a share
 * sheet it opens LinkedIn's own share page. Every LinkedIn route is recorded
 * as channel "linkedin".
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cachedShareLink, createShareLink, linkForChannel, ShareChannel, ShareTargetKind } from './api';
import { tcTrack } from './track';

interface Props {
  /** The technology shared (when `target` is not given). */
  techId?: string;
  /** Anything else shareable: an OCEAN problem / company card. */
  target?: { kind: ShareTargetKind; id: string };
  title: string;
  /** Tracking: where the card sits ('search' | 'popular' | 'modal' …). */
  surface: string;
  onToast: (msg: string) => void;
  /** The trigger: the round share icon unless a host gives its own class / content. */
  buttonClassName?: string;
  buttonContent?: React.ReactNode;
  /** "Share this technology" unless the host names its target. */
  label?: string;
  /** Tracking calls (default: the tech card's tcTrack). */
  track?: (action: string, props: Record<string, unknown>) => void;
  /** Open the menu on mount (a host that first had to prepare the target, e.g. an OCEAN collection's link). */
  defaultOpen?: boolean;
}

/** What a menu item does: a recorded channel, or one of LinkedIn's routes (recorded as 'linkedin'). */
type ShareAction = ShareChannel | 'linkedin_message' | 'linkedin_phone';
const WINDOW_ACTIONS = new Set<ShareAction>(['linkedin', 'linkedin_message', 'whatsapp', 'x']);
const recordedAs = (a: ShareAction): ShareChannel => (a === 'linkedin_message' || a === 'linkedin_phone' ? 'linkedin' : a);
const LINKEDIN_MESSAGES = 'https://www.linkedin.com/messaging/';
const linkedinOffsite = (url: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

/** A phone or tablet: a coarse pointer and the system share sheet. */
const onPhone = (): boolean => {
  try {
    return typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function'
      && !!window.matchMedia?.('(pointer: coarse)').matches;
  } catch { return false; }
};

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall back below */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

const targetFor = (channel: ShareChannel, url: string, title: string): string => {
  const enc = encodeURIComponent;
  switch (channel) {
    // LinkedIn cannot be sent to one chat from a web page (tracker, 2026-10-29): it
    // opens the post composer with the title and link already written, so the
    // post says what it is (share-offsite posted the bare link).
    case 'linkedin': return `https://www.linkedin.com/feed/?shareActive=true&text=${enc(`${title}\n\n${url}`)}`;
    case 'whatsapp': return `https://wa.me/?text=${enc(`${title} ${url}`)}`;
    case 'x': return `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
    case 'email': return `mailto:?subject=${enc(title)}&body=${enc(`${title}\n${url}`)}`;
    default: return url;
  }
};

export const ShareIcon: React.FC = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

export const ShareMenu: React.FC<Props> = ({ techId, target, title, surface, onToast, buttonClassName, buttonContent,
                                            label = 'Share this technology', track = tcTrack, defaultOpen = false }) => {
  const kind: ShareTargetKind = target?.kind ?? 'tech';
  const targetId = target?.id ?? techId ?? '';
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState<ShareAction | null>(null);
  const [manual, setManual] = useState('');   // a link the clipboard refused
  // A phone refused the share sheet after the first link fetch (the tap had
  // expired): the next tap has the link at hand and opens it at once.
  const [sheetAgain, setSheetAgain] = useState(false);
  const phone = onPhone();
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Place the menu under the button (right-aligned), or above it when the
  // viewport has no room below — the card may sit at the bottom of a modal.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect();
      const m = menuRef.current;
      if (!b || !m) return;
      const h = m.offsetHeight;
      const w = m.offsetWidth;
      const below = b.bottom + 8 + h <= window.innerHeight - 8;
      const top = below ? b.bottom + 8 : Math.max(8, b.top - 8 - h);
      const left = Math.min(Math.max(8, b.right - w), window.innerWidth - w - 8);
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, manual]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    // Keyboard users land on the first item.
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) track('techcard_share_open', { tech_id: targetId, target_kind: kind, surface });
    setManual('');
    setSheetAgain(false);
    setPos(null);
    setOpen(o => !o);
  };

  const act = async (action: ShareAction) => {
    if (busy) return;
    const channel = recordedAs(action);
    track('techcard_share', { channel, route: action !== channel ? action : undefined, tech_id: targetId,
                              target_kind: kind, surface });
    const cached = cachedShareLink(kind, targetId, 'platform');
    // Opened inside the click, before any await, or the browser blocks it.
    let win: Window | null = null;
    if (!cached && WINDOW_ACTIONS.has(action)) {
      try { win = window.open('', '_blank'); if (win) win.opener = null; } catch { win = null; }
    }
    const posting = createShareLink({ target_kind: kind, target_id: targetId, surface: 'platform', channel });
    let url = cached ? linkForChannel(cached.url, channel) : '';
    if (!url) {
      setBusy(action);
      const link = await posting;
      setBusy(null);
      url = link?.url || '';
    }
    if (!url) {
      try { win?.close(); } catch { /* ignore */ }
      track('techcard_share_error', { channel, tech_id: targetId, target_kind: kind, surface });
      onToast('Could not create a share link — please try again.');
      return;
    }

    if (channel === 'copy') {
      const ok = await copyText(url);
      if (ok) { onToast('Link copied'); setOpen(false); }
      else setManual(url);   // keep the menu open with the link to copy by hand
      return;
    }
    if (channel === 'native') {
      try {
        await (navigator as any).share({ title, url });
        setOpen(false);
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          const ok = await copyText(url);
          if (ok) { onToast('Link copied'); setOpen(false); } else setManual(url);
        }
      }
      return;
    }
    if (action === 'linkedin_phone') {
      if (typeof (navigator as any).share !== 'function') {
        window.location.href = linkedinOffsite(url);
        setOpen(false);
        return;
      }
      try {
        await (navigator as any).share({ title, text: title, url });
        setOpen(false);
      } catch (e: any) {
        // NotAllowedError: the wait for the link used up the tap. The link is
        // kept now, so one more tap opens the sheet at once.
        if (e?.name === 'NotAllowedError' && !cached) setSheetAgain(true);
        else if (e?.name !== 'AbortError') { window.location.href = linkedinOffsite(url); setOpen(false); }
      }
      return;
    }
    if (action === 'linkedin_message') {
      const ok = await copyText(url);
      const go = LINKEDIN_MESSAGES;
      if (win) { try { win.location.href = go; } catch { window.open(go, '_blank', 'noopener,noreferrer'); } }
      else window.open(go, '_blank', 'noopener,noreferrer');
      if (ok) { onToast('Link copied. Paste it into a LinkedIn chat.'); setOpen(false); }
      else setManual(url);
      return;
    }
    const href = targetFor(channel, url, title);
    if (channel === 'email') { window.location.href = href; setOpen(false); return; }
    if (win) { try { win.location.href = href; } catch { window.open(href, '_blank', 'noopener,noreferrer'); } }
    else window.open(href, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const item = (action: ShareAction, label: string) => (
    <button
      key={action}
      type="button"
      role="menuitem"
      className="tcx-menu-item"
      aria-disabled={busy ? true : undefined}
      onClick={e => { e.stopPropagation(); void act(action); }}
    >
      {label}{busy === action ? '…' : ''}
    </button>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={buttonClassName ?? 'tcx-icon-btn'}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Share"
        onClick={toggle}
      >
        {buttonContent ?? <ShareIcon />}
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className="tcx tcx-menu"
          role="menu"
          aria-label={label}
          style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="tcx-menu-head">Share</div>
          {/* Three channels (owner, 2026-10-18): e-mail did nothing where no mail app
              is set up, and X / the system sheet were not wanted. */}
          {item('copy', 'Copy link')}
          {item('whatsapp', 'WhatsApp')}
          {phone ? item('linkedin_phone', sheetAgain ? 'Tap again to share on LinkedIn' : 'LinkedIn') : (
            <>
              {item('linkedin', 'Post on LinkedIn')}
              {item('linkedin_message', 'Send in a LinkedIn message')}
            </>
          )}
          {manual && (
            <div className="tcx-menu-link">
              <input readOnly value={manual} aria-label="Share link" onFocus={e => e.currentTarget.select()} autoFocus />
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ShareMenu;
